import { useMemo, useState } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import BasePlot from "./BasePlot";
import PlotGrid from "./PlotGrid";

const Plot = createPlotlyComponent(Plotly);

interface Props {
  backendResult: any;
}

const squareLayout = {
  autosize: true,
  margin: { l: 55, r: 35, t: 20, b: 55 },
  paper_bgcolor: "white",
  plot_bgcolor: "white",
  hovermode: "closest" as const,
};

const TrajectoryPlots = ({ backendResult }: Props) => {
  const [pagaMarkerSize, setPagaMarkerSize] = useState(34);
  const [umapMarkerSize, setUmapMarkerSize] = useState(9);
  const nodes = useMemo(() => Array.isArray(backendResult?.pagaGraph?.nodes) ? backendResult.pagaGraph.nodes : [], [backendResult?.pagaGraph?.nodes]);
  const edges = useMemo(() => Array.isArray(backendResult?.pagaGraph?.edges) ? backendResult.pagaGraph.edges : [], [backendResult?.pagaGraph?.edges]);
  const points = useMemo(() => Array.isArray(backendResult?.trajectoryPlot) ? backendResult.trajectoryPlot : [], [backendResult?.trajectoryPlot]);

  const nodeById = useMemo(
    () => new Map(nodes.map((node: any) => [String(node.id), node])),
    [nodes],
  );
  const pagaData = useMemo(() => {
    const edgeTraces = edges.map((edge: any) => {
      const source: any = nodeById.get(String(edge.source));
      const target: any = nodeById.get(String(edge.target));
      if (!source || !target) return null;
      return {
        type: "scatter",
        mode: "lines",
        x: [source.x, target.x],
        y: [source.y, target.y],
        line: { color: "#94a3b8", width: Math.max(1.5, Number(edge.weight || 0) * 8) },
        hoverinfo: "text",
        text: [`${edge.source} → ${edge.target}<br>connectivity: ${Number(edge.weight).toFixed(3)}`],
        showlegend: false,
      };
    }).filter(Boolean);
    const regular = nodes.filter((node: any) => !node.isRoot);
    const roots = nodes.filter((node: any) => node.isRoot);
    return [
      ...edgeTraces,
      {
        type: "scatter",
        mode: "markers+text",
        x: regular.map((node: any) => node.x),
        y: regular.map((node: any) => node.y),
        text: regular.map((node: any) => node.label),
        textposition: "top center",
        customdata: regular.map((node: any) => node.cellCount),
        hovertemplate: "Cluster %{text}<br>%{customdata} cells<extra></extra>",
        marker: { size: pagaMarkerSize, color: "#19C1DB", line: { color: "white", width: 2 } },
        name: "Cluster",
      },
      {
        type: "scatter",
        mode: "markers+text",
        x: roots.map((node: any) => node.x),
        y: roots.map((node: any) => node.y),
        text: roots.map((node: any) => node.label),
        textposition: "top center",
        customdata: roots.map((node: any) => node.cellCount),
        hovertemplate: "Root cluster %{text}<br>%{customdata} cells<extra></extra>",
        marker: { size: pagaMarkerSize + 8, color: "#ef4444", symbol: "star", line: { color: "#991b1b", width: 2 } },
        name: "Root cluster",
      },
    ];
  }, [edges, nodes, nodeById, pagaMarkerSize]);

  const regularPoints = points.filter((point: any) => !point.is_root);
  const rootPoints = points.filter((point: any) => point.is_root);
  const umapData: any[] = [
    {
      type: "scattergl",
      mode: "markers",
      x: regularPoints.map((point: any) => point.UMAP1),
      y: regularPoints.map((point: any) => point.UMAP2),
      text: regularPoints.map((point: any) => point.cell_id),
      customdata: regularPoints.map((point: any) => point.dpt_pseudotime),
      hovertemplate: "%{text}<br>DPT pseudotime: %{customdata:.4f}<extra></extra>",
      marker: {
        size: umapMarkerSize,
        color: regularPoints.map((point: any) => point.dpt_pseudotime),
        colorscale: "Viridis",
        colorbar: { title: { text: "DPT<br>pseudotime" }, thickness: 14 },
        opacity: 0.82,
      },
      name: "Cells",
    },
    {
      type: "scatter",
      mode: "markers",
      x: rootPoints.map((point: any) => point.UMAP1),
      y: rootPoints.map((point: any) => point.UMAP2),
      text: rootPoints.map((point: any) => point.cell_id),
      hovertemplate: "Selected root cell<br>%{text}<extra></extra>",
      marker: { size: umapMarkerSize + 8, color: "#ef4444", symbol: "star", line: { color: "white", width: 1.5 } },
      name: "Selected root",
    },
  ];

  if (!nodes.length && !points.length) return <p>No trajectory plot data available.</p>;

  return (
    <PlotGrid columns={2}>
      {nodes.length ? (
        <BasePlot
          title="PAGA Cluster Connectivity"
          showSizeSlider
          defaultSize={34}
          onSizeChange={setPagaMarkerSize}
          downloadFilename="trajectory_paga"
        >
          <Plot
            data={pagaData as any}
            layout={{
              ...squareLayout,
              xaxis: { visible: false, scaleanchor: "y", scaleratio: 1 },
              yaxis: { visible: false },
              showlegend: true,
              legend: { orientation: "h", y: -0.08 },
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: "100%", height: "100%" }}
            className="plotly-plot"
          />
        </BasePlot>
      ) : null}
      {points.length ? (
        <BasePlot
          title="UMAP Pseudotime"
          showSizeSlider
          defaultSize={9}
          onSizeChange={setUmapMarkerSize}
          downloadFilename="trajectory_umap_pseudotime"
        >
          <Plot
            data={umapData}
            layout={{
              ...squareLayout,
              xaxis: { title: { text: "UMAP 1" }, scaleanchor: "y", scaleratio: 1 },
              yaxis: { title: { text: "UMAP 2" } },
              showlegend: true,
              legend: { orientation: "h", y: -0.2 },
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: "100%", height: "100%" }}
            className="plotly-plot"
          />
        </BasePlot>
      ) : null}
    </PlotGrid>
  );
};

export default TrajectoryPlots;
